import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, MessageCircle, MapPin, Facebook, Instagram, Linkedin } from 'lucide-react';
import logo from '../assets/trendrush-logo.png';

const Footer = () => {
  return (
    <footer className="bg-gray-900 text-white">
      <div className="container mx-auto px-6 py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
          {/* Brand Section */}
          <div className="lg:col-span-2 space-y-6">
            <div className="flex items-center gap-3">
              <img 
                src={logo} 
                alt="Trendrush Logo" 
                className="h-12 w-auto"
              />
              <span className="text-2xl font-bold text-white">Trendrush</span>
            </div>
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-white">About Us</h3>
              <p className="text-gray-400 leading-relaxed max-w-2xl text-sm">
                TrendRush is a hyperlocal fashion and lifestyle commerce platform built for modern India. 
                We exist for moments when personal style matters—but time doesn't cooperate. TrendRush connects customers with trusted local and premium fashion brands through a technology-first ecosystem that enables fast, seamless, and reliable access to curated fashion.
              </p>
              <p className="text-gray-400 leading-relaxed max-w-2xl text-sm">
                By blending intelligent discovery, proximity-based logistics, and brand-centric retail partnerships, TrendRush delivers a boutique-quality shopping experience while empowering retailers to reach nearby customers with precision and control.
              </p>
              <p className="text-gray-400 leading-relaxed max-w-2xl text-sm italic">
                We believe fashion is confidence in motion—and TrendRush is designed to move with it.
              </p>
            </div>
            <div className="flex gap-4">
              <a 
                href="https://www.facebook.com/profile.php?id=61577359380007" 
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-lg bg-gray-800 flex items-center justify-center text-gray-400 hover:bg-coral hover:text-white transition-all"
              >
                <Facebook className="w-5 h-5" />
              </a>
              <a 
                href="https://www.instagram.com/trendrushindia/" 
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-lg bg-gray-800 flex items-center justify-center text-gray-400 hover:bg-coral hover:text-white transition-all"
              >
                <Instagram className="w-5 h-5" />
              </a>
              <a 
                href="https://www.linkedin.com/company/trendrushindia/?viewAsMember=true" 
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-lg bg-gray-800 flex items-center justify-center text-gray-400 hover:bg-coral hover:text-white transition-all"
              >
                <Linkedin className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-lg font-bold mb-4 text-white">Quick Links</h3>
            <ul className="space-y-3">
              <li>
                <a href="#how-it-works" className="text-gray-400 hover:text-coral transition-colors">
                  How It Works
                </a>
              </li>
              <li>
                <a href="#for-retailers" className="text-gray-400 hover:text-coral transition-colors">
                  For Retailers
                </a>
              </li>
              <li>
                <a href="#for-consumers" className="text-gray-400 hover:text-coral transition-colors">
                  For Consumers
                </a>
              </li>
              <li>
                <a href="#faq" className="text-gray-400 hover:text-coral transition-colors">
                  FAQ
                </a>
              </li>
              <li>
                <a href="#contact" className="text-gray-400 hover:text-coral transition-colors">
                  Contact
                </a>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-lg font-bold mb-4 text-white">Get in Touch</h3>
            <ul className="space-y-4">
              <li>
                <a 
                  href="mailto:support@trendrushindia.com" 
                  className="flex items-start gap-3 text-gray-400 hover:text-coral transition-colors group"
                >
                  <Mail className="w-5 h-5 flex-shrink-0 mt-0.5 group-hover:scale-110 transition-transform" />
                  <span className="text-sm">support@trendrushindia.com</span>
                </a>
              </li>
              <li>
                <a 
                  href="https://wa.me/918247034033?text=Hi%2C%20I%20want%20to%20know%20more%20about%20Trendrush" 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-start gap-3 text-gray-400 hover:text-coral transition-colors group"
                >
                  <MessageCircle className="w-5 h-5 flex-shrink-0 mt-0.5 group-hover:scale-110 transition-transform" />
                  <span className="text-sm">+91 8247034033</span>
                </a>
              </li>
              <li className="flex items-start gap-3 text-gray-400">
                <MapPin className="w-5 h-5 flex-shrink-0 mt-0.5" />
                <span className="text-sm">India</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Support Emails */}
        <div className="border-t border-gray-800 pt-8 mb-8">
          <div className="grid sm:grid-cols-3 gap-6">
            <div className="text-left">
              <p className="text-sm text-gray-500 mb-2">Merchant Support</p>
              <a 
                href="mailto:merchant-support@trendrushindia.com" 
                className="text-sm text-gray-300 hover:text-coral transition-colors block break-all"
              >
                merchant-support@trendrushindia.com
              </a>
            </div>
            <div className="text-left">
              <p className="text-sm text-gray-500 mb-2">Customer Support</p>
              <a 
                href="mailto:customer-support@trendrushindia.com" 
                className="text-sm text-gray-300 hover:text-coral transition-colors block break-all"
              >
                customer-support@trendrushindia.com
              </a>
            </div>
            <div className="text-left">
              <p className="text-sm text-gray-500 mb-2">General Inquiries</p>
              <a 
                href="mailto:support@trendrushindia.com" 
                className="text-sm text-gray-300 hover:text-coral transition-colors block break-all"
              >
                support@trendrushindia.com
              </a>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-800 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="text-sm text-gray-500 text-center md:text-left">
              <p>© {new Date().getFullYear()} Trendrush. All rights reserved.</p>
              <p className="mt-1 text-xs text-gray-400">
                Made with <span className="text-coral">❤️</span> in India.
              </p>
            </div>
            <div className="flex gap-6">
              <Link to="/privacy-policy" className="text-sm text-gray-500 hover:text-coral transition-colors">
                Privacy Policy
              </Link>
              <Link to="/terms-of-service" className="text-sm text-gray-500 hover:text-coral transition-colors">
                Terms of Service
              </Link>
              <Link to="/cookie-policy" className="text-sm text-gray-500 hover:text-coral transition-colors">
                Cookie Policy
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
